import {
  faCheckCircle,
  faExclamationTriangle,
  faTimesCircle,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, useLingui } from '@lingui/react/macro';
import { useAtomValue } from 'jotai';

import { selectedPatientZuubDataAtom, selectedPlanAtom } from '../../atoms';
import { isPlanActive } from '../../helpers';

import { BenefitsProgress } from './BenefitsProgress';

export const DentalCareSection = () => {
  const currentPatientData = useAtomValue(selectedPatientZuubDataAtom);
  const currentPlan = useAtomValue(selectedPlanAtom);

  const { t } = useLingui();

  // Destructure benefit summary data
  const benefitSummary = currentPlan?.benefitSummary;
  const remainingBenefits = benefitSummary?.remainingBenefits || 0;
  const totalBenefits = benefitSummary?.totalBenefits || 0;
  const remainingDeductible =
    benefitSummary?.deductible?.remainingDeductible || 0;
  const totalDeductible = benefitSummary?.deductible?.totalDeductible || 0;
  const isDeductibleMet = benefitSummary?.deductible?.status === 'Met';

  // Get plan status and last run information
  const planStatus = currentPlan?.plan?.status;
  const isActive = isPlanActive(planStatus || null);
  const lastRun = currentPatientData?.lastRun || null;

  // Format the last run date
  const formatLastRunDate = (dateString: string | null) => {
    if (!dateString) return 'Never';

    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
      });
    } catch {
      return 'Invalid date';
    }
  };

  return (
    <div className="vai-space-y-6">
      {/* Plan Status and Benefits Sections */}
      <div className="vai-flex vai-gap-8">
        {/* Plan Status */}
        <div>
          <div className="vai-text-sm vai-font-medium vai-text-muted-foreground vai-uppercase vai-leading-small">
            <Trans>PLAN STATUS</Trans>
          </div>

          <div className="vai-mt-3 vai-flex vai-items-center vai-gap-2">
            <FontAwesomeIcon
              icon={isActive ? faCheckCircle : faTimesCircle}
              className={`vai-h-4 vai-w-4 ${
                isActive ? 'vai-text-green-700' : 'vai-text-red-500'
              }`}
            />
            <span
              className={`${
                isActive
                  ? 'vai-text-green-700 vai-text-2xl vai-font-medium'
                  : 'vai-text-red-600 vai-text-2xl vai-font-medium'
              }`}
            >
              {isActive ? 'Active' : 'Inactive'}
            </span>
          </div>

          {lastRun && (
            <div className="vai-mt-2 vai-text-sm vai-text-gray-500">
              <Trans>Last run:</Trans> {formatLastRunDate(lastRun)}
            </div>
          )}
        </div>

        {/* Vertical Divider */}
        <div className="vai-w-px vai-bg-gray-200"></div>

        {/* Dental Benefits Remaining */}
        <div className="vai-flex-1">
          {remainingBenefits !== undefined && totalBenefits !== undefined && (
            <BenefitsProgress
              progress={remainingBenefits}
              total={totalBenefits}
              title={t`DENTAL BENEFITS REMAINING`}
              hideProgress={remainingBenefits === 0}
              chip={
                remainingBenefits === 0
                  ? {
                      text: t`Benefits Exhausted`,
                      variant: 'error',
                      icon: <FontAwesomeIcon icon={faExclamationTriangle} />,
                    }
                  : undefined
              }
            />
          )}
        </div>

        {/* Vertical Divider */}
        <div className="vai-w-px vai-bg-gray-200"></div>

        {/* Deductible Progress */}
        <div className="vai-flex-1">
          {totalDeductible !== undefined &&
            remainingDeductible !== undefined && (
              <BenefitsProgress
                progress={remainingDeductible}
                total={totalDeductible}
                title={t`DEDUCTIBLE REMAINING`}
                progressColor="vai-bg-primary"
                hideProgress={isDeductibleMet}
                chip={
                  isDeductibleMet
                    ? {
                        text: t`Deductible Met`,
                        variant: 'success',
                      }
                    : undefined
                }
              />
            )}
        </div>
      </div>
    </div>
  );
};
